/* ============================================================
   THE SLEEP LOFT — shared reveal-on-scroll observer.
   ------------------------------------------------------------
   One IntersectionObserver for EVERY [data-sl-reveal] container
   on the page, replacing the near-identical observer that each
   custom section used to inline.

   - Adds `sl-reveal-ready` to <html> immediately, which "arms"
     the hidden initial state in sl-shared.css. If this script
     fails to load, that class is never set and all .sl-reveal
     content stays visible (no hidden above-the-fold content).
   - Adds `.sl-in` to a container when it scrolls into view; the
     CSS then animates its `.sl-reveal` children (staggered via
     each child's inline `--sl-delay`).
   - Re-scans on `shopify:section:load` so the theme editor and
     dynamically-loaded sections animate correctly.
   ============================================================ */
(function () {
  var root = document.documentElement;
  root.classList.add('sl-reveal-ready');

  var supported = 'IntersectionObserver' in window;
  var io = supported
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('sl-in');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -10% 0px' })
    : null;

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll('[data-sl-reveal]:not(.sl-in)');
    for (var i = 0; i < nodes.length; i++) {
      if (io) {
        io.observe(nodes[i]);
      } else {
        nodes[i].classList.add('sl-in'); // no IO support: reveal immediately
      }
    }
  }

  if (document.readyState !== 'loading') {
    scan();
  } else {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  }

  // Theme editor / dynamic section injection
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target || document);
  });
})();

/* ============================================================
   THE SLEEP LOFT — policy page quick-nav.
   ------------------------------------------------------------
   Shopify renders shop policy pages (refund / privacy / terms /
   shipping) with fixed .shopify-policy__* markup and ignores
   custom theme templates, so we enhance them client-side:
   gather the real policy links already on the page (footer) and
   inject a pill quick-nav at the top of the policy, highlighting
   the current page. Pure progressive enhancement — if anything
   is missing it simply does nothing.
   ============================================================ */
(function () {
  if (!document.body || document.body.className.indexOf('template-policy') === -1) return;

  function buildNav() {
    var container = document.querySelector('.shopify-policy__container');
    if (!container || container.querySelector('.sl-policy-nav')) return;

    var seen = {}, links = [];
    var anchors = document.querySelectorAll('a[href*="/policies/"]');
    for (var i = 0; i < anchors.length; i++) {
      var raw = anchors[i].getAttribute('href') || '';
      var path = raw.split('?')[0].split('#')[0].replace(/^https?:\/\/[^\/]+/, '');
      if (path.indexOf('/policies/') !== 0) continue;
      path = path.replace(/\/$/, '');
      if (seen[path]) continue;
      var label = (anchors[i].textContent || '').replace(/\s+/g, ' ').trim();
      if (!label) continue;
      seen[path] = 1;
      links.push({ path: path, label: label });
    }
    if (links.length < 2) return;

    var current = location.pathname.replace(/\/$/, '');
    var nav = document.createElement('nav');
    nav.className = 'sl-policy-nav';
    nav.setAttribute('aria-label', 'Policies');
    var html = '<span class="sl-policy-nav__label">Policies</span><ul>';
    for (var j = 0; j < links.length; j++) {
      var active = links[j].path === current ? ' class="is-active" aria-current="page"' : '';
      html += '<li><a href="' + links[j].path + '"' + active + '>' + links[j].label + '</a></li>';
    }
    html += '</ul>';
    nav.innerHTML = html;
    container.insertBefore(nav, container.firstChild);
  }

  if (document.readyState !== 'loading') {
    buildNav();
  } else {
    document.addEventListener('DOMContentLoaded', buildNav);
  }
})();

/* ============================================================
   THE SLEEP LOFT — lazy-load Trustindex review widgets.
   ------------------------------------------------------------
   The homepage embeds ~12 Trustindex widgets. Loading every
   widget's loader.js up front is the #1 cause of Total Blocking
   Time (main-thread JS). Each placeholder is now a
   <div class="ti-lazy" data-ti-src="...loader.js?ID">; we inject
   the real script only when the widget nears the viewport, so
   the reviews still render but the work happens off the initial
   load. Belt-and-suspenders fallbacks guarantee reviews ALWAYS
   appear: first user interaction loads everything, and a hard
   timeout loads anything still pending (covers no-scroll bots).
   ============================================================ */
(function () {
  var pending = [];
  function collect() {
    var nodes = document.querySelectorAll('.ti-lazy[data-ti-src]');
    for (var i = 0; i < nodes.length; i++) pending.push(nodes[i]);
  }

  function load(el) {
    if (!el || el.getAttribute('data-ti-loaded')) return;
    el.setAttribute('data-ti-loaded', '1');
    var s = document.createElement('script');
    s.async = true;
    s.src = el.getAttribute('data-ti-src');
    // Insert right where the placeholder sits so Trustindex renders in place.
    el.parentNode.insertBefore(s, el.nextSibling);
  }

  function loadAll() {
    for (var i = 0; i < pending.length; i++) load(pending[i]);
  }

  function init() {
    collect();
    if (!pending.length) return;

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { load(entry.target); io.unobserve(entry.target); }
        });
      }, { rootMargin: '700px 0px' }); // load a bit before they scroll in
      for (var i = 0; i < pending.length; i++) io.observe(pending[i]);
    } else {
      loadAll();
      return;
    }

    // Fallback 1: first real interaction loads everything immediately.
    var once = function () { loadAll(); remove(); };
    var remove = function () {
      ['scroll', 'pointerdown', 'touchstart', 'keydown'].forEach(function (ev) {
        window.removeEventListener(ev, once, { passive: true });
      });
    };
    ['scroll', 'pointerdown', 'touchstart', 'keydown'].forEach(function (ev) {
      window.addEventListener(ev, once, { passive: true });
    });

    // Fallback 2: hard timeout so bots / no-scroll sessions still get reviews.
    setTimeout(loadAll, 5000);
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();

/* ============================================================
   THE SLEEP LOFT — ?directions=1 deep link.  (2026-08-25)
   ------------------------------------------------------------
   External listings (Yelp, Google Business Profile, Bing) can
   only point at ONE url, so they cannot render our multi-app
   "Get directions" chooser themselves. Landing on a showroom
   page with ?directions=1 opens that chooser automatically, so
   an off-site button gives the same Google / Apple / Waze /
   Bing choice the website does.

   Two deliberate choices:

   1. We CLICK the page's own primary directions button rather
      than rebuilding the modal here. The chooser is built by
      inline scripts on the location sections; driving it with a
      real click means this keeps working if that markup or its
      handler changes.

   2. The selector is scoped to .sl_loc_template27_primary. The
      footer carries ~20 other .sl-maps-link elements — one per
      showroom — so querying the first .sl-maps-link on the page
      would happily open directions to the WRONG store.

   Fails silent: no param, no matching button, or any error and
   the page behaves exactly as it does today.
   ============================================================ */
(function () {
  if (!/[?&]directions=1(?:&|$)/.test(window.location.search)) return;

  var SELECTOR = '.sl_loc_template27_primary.sl-maps-link';
  var POLL_MS = 125;
  var MAX_TRIES = 40; // ~5s, then give up quietly

  function openChooser() {
    var btn = document.querySelector(SELECTOR);
    if (!btn) return false;
    btn.click();
    return true;
  }

  function start() {
    if (openChooser()) return;

    // The location section can render after DOMContentLoaded.
    var tries = 0;
    var timer = setInterval(function () {
      if (openChooser() || ++tries > MAX_TRIES) clearInterval(timer);
    }, POLL_MS);
  }

  if (document.readyState !== 'loading') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
